import { uiText } from '../i18n/uiText';
import React, { useEffect, useMemo, useState } from 'react';
import useDraftBuffer from '../hooks/useDraftBuffer';
import FormDraftNotice from '../components/common/FormDraftNotice';
import Dialog from '../components/common/Dialog';
import SafeDateInput from '../components/common/SafeDateInput';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { issuesApi, usersApi, opsApi, getApiErrorMessage } from '../api';
import { useAuth } from '../store/AuthContext';
import { Edit2, Trash2, AlertTriangle, X, Download, ArrowLeftRight, Search } from 'lucide-react';
import PageHeader from '../components/common/PageHeader';
import { useToast } from '../store/ToastContext';
import { isAuditeeRole } from '../utils/permissions';
import { useConfirm } from '../store/ConfirmContext';
import StatusBadge from '../components/ui/StatusBadge';
import ActionMenu from '../components/common/ActionMenu';
import RegistryPreferences, { useRegistrySettings, sortRegistryRows } from '../components/registry/RegistryPreferences';
import RecordPreview from '../components/registry/RecordPreview';
import registryStyles from '../components/registry/RegistryWorkspace.module.css';
const REGISTRY_COLUMNS = [{key:'title',label:uiText("Замечание"),required:true},{key:'audit',label:uiText("Аудит")},{key:'priority',label:uiText("Приоритет")},{key:'status',label:uiText("Статус")},{key:'assignee',label:uiText("Исполнитель")},{key:'due',label:uiText("Срок ПКПД")},{key:'actions',label:uiText("Действия"),required:true}];

const STATUS_OPTIONS = [
  ['all', uiText("Все статусы")],
  ['open', uiText("Открытые")],
  ['in_progress', uiText("В работе")],
  ['resolved', uiText("Решённые")],
  ['closed', uiText("Закрытые")],
];

const PRIORITY_OPTIONS = [
  ['all', uiText("Все классификации")],
  ['major', uiText("Значительное (Major)")],
  ['ofi', uiText("Возможность для улучшения (OFI)")],
  ['sp', uiText("Сильная сторона (SP)")],
  ['significant', uiText("Значительное")],
  ['minor', uiText("Незначительное")],
  ['recommendation', uiText("Рекомендация аудитора")],
  ['critical', uiText("Критический")],
  ['high', uiText("Высокий")],
  ['medium', uiText("Средний")],
  ['low', uiText("Низкий")],
];

const classificationLabel = (priority, scheme) => (
  scheme === 'compliance-v2' ? {major: uiText("Значительное (Major)"), minor: uiText("Незначительное (Minor)"), ofi: uiText("Возможность для улучшения (OFI)"), sp: uiText("Сильная сторона (SP)")}[priority]
    : PRIORITY_OPTIONS.find(([value]) => value === priority)?.[1]
) || priority;

const STATUS_META = {
  open: { label: uiText("Открыто"), cls: 'badge-red' },
  in_progress: { label: uiText("В работе"), cls: 'badge-blue' },
  fixed: { label: uiText("Исполнение заявлено — ожидает проверки"), cls: 'badge-orange' },
  verified: { label: uiText("Подтверждено аудитором"), cls: 'badge-green' },
  reopened: { label: uiText("Возвращено в работу"), cls: 'badge-orange' },
  escalated: { label: uiText("Просрочено — требуется внимание"), cls: 'badge-red' },
  resolved: { label: uiText("Решено"), cls: 'badge-green' },
  closed: { label: uiText("Закрыто"), cls: 'badge-gray' },
};

const EDITABLE_STATUS_OPTIONS = [
  ['open', uiText("Открыто")],
  ['in_progress', uiText("В работе")],
  ['fixed', uiText("Исполнение заявлено — ожидает проверки")],
  ['verified', uiText("Подтверждено аудитором")],
  ['reopened', uiText("Возвращено в работу")],
  ['escalated', uiText("Просрочено — требуется внимание")],
  ['resolved', uiText("Решено")],
  ['closed', uiText("Закрыто")],
];

const STATUS_GROUPS = {
  open: new Set(['open', 'reopened', 'escalated']),
  in_progress: new Set(['in_progress', 'fixed']),
  resolved: new Set(['resolved', 'verified']),
  closed: new Set(['closed']),
};

const TERMINAL_STATUSES = new Set(['resolved', 'verified', 'closed']);

function statusGroupOf(status) {
  return Object.keys(STATUS_GROUPS).find(group => STATUS_GROUPS[group].has(status)) || 'open';
}

function variantFromBadgeClass(className) {
  if (className?.includes('green')) return 'success';
  if (className?.includes('orange')) return 'warning';
  if (className?.includes('red')) return 'danger';
  if (className?.includes('blue')) return 'info';
  return 'neutral';
}

function formatDate(dt) {
  if (!dt) return '—';
  try {
    return new Date(dt).toLocaleDateString('ru-RU');
  } catch {
    return dt;
  }
}

function ActiveFilterChip({ label, onRemove }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 999, background: '#f4f4f5', fontSize: 12 }}>
      <span>{label}</span>
      <button type="button" aria-label={uiText("Удалить фильтр")} title={uiText("Удалить фильтр")} onClick={onRemove} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
        <X size={12} />
      </button>
    </div>
  );
}

export function IssueModal({ issue, users, onClose, onSave, currentUserRole }) {
  const { user } = useAuth();
  const buffer = useDraftBuffer(user?.id, issue.id, 'edit-issue');
  // Бага 12: проверяемый может только назначить ответственного и срок исполнения,
  // но не изменять характеристики несоответствия (название, описание, классификацию, статус).
  const isCustomer = isAuditeeRole(currentUserRole);
  const [initialForm] = useState({
    title: issue?.title || '',
    description: issue?.description || '',
    requirement_ref: issue?.requirement_ref || '',
    priority: issue?.priority || 'minor',
    status: issue?.status || 'open',
    // Бага 13: подтянем id ответственного из любого доступного поля бэкенда
    assigned_to: issue?.assignee?.id ?? issue?.assigned_to ?? issue?.assigned_to_id ?? '',
    due_date: issue?.due_date ? String(issue.due_date).slice(0, 10) : '',
  });
  const [saving, setSaving] = useState(false);
  const form = buffer.values.form || initialForm;
  const setForm = update => { if (!saving && !buffer.recovery) buffer.edit('form', typeof update === 'function' ? update(buffer.values.form || initialForm) : update); };

  // Если в users нет ответственного из issue — добавим его, чтобы select показал «Смирнова»
  // (Бага 13: дропдаун обязан показать текущее значение, даже если этого пользователя нет в общем списке)
  const usersForDropdown = useMemo(() => {
    const list = Array.isArray(users) ? [...users] : [];
    const aid = issue?.assignee?.id ?? issue?.assigned_to ?? null;
    if (aid && !list.some(u => String(u.id) === String(aid))) {
      list.unshift({ id: aid, full_name: issue?.assignee?.full_name || uiText("Пользователь #{v0}", {v0: (aid)}) });
    }
    return list;
  }, [users, issue]);

  const submit = async (e) => {
    e.preventDefault();
    if (saving || buffer.recovery) return;
    const snapshot = buffer.snapshot();
    if (isCustomer) {
      onClose();
      return;
    }
    if (!isCustomer && !form.title.trim()) return alert(uiText("Укажи название замечания"));
    setSaving(true);
    try {
      // Для customer на сервер отправляем только разрешённые ему поля
      const payload = isCustomer
        ? {}
        : {
            title: form.title.trim(),
            description: form.description,
            requirement_ref: form.requirement_ref,
            priority: form.priority,
            status: form.status,
            assigned_to: form.assigned_to ? Number(form.assigned_to) : null,
            due_date: form.due_date || null,
          };
      if (await onSave(payload)) buffer.acknowledge(snapshot, { committed: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog labelledBy="issue-edit-title" onClose={onClose} closeDisabled={saving}>
        <div className="ap-dialog__header">
          <div className="ap-dialog__title" id="issue-edit-title" data-dialog-heading tabIndex={-1}>{isCustomer ? uiText("Карточка замечания") : uiText("Редактирование замечания")}</div>
          <button type="button" className="ap-dialog__close" disabled={saving} aria-label={uiText("Закрыть")} title={uiText("Закрыть")} onClick={onClose}><X size={18} /></button>
        </div>
        <form className="issue-edit-form" onSubmit={submit}>
          <div className="ap-dialog__body">
            {!isCustomer && <FormDraftNotice buffer={buffer} label={uiText("Замечание")} disabled={saving} />}
            {isCustomer ? (
              <div className="alert" style={{ background: '#fffbf0', border: '1px solid #fed7aa', color: '#92400e', marginBottom: 14 }}>{uiText("Характеристики несоответствия фиксирует аудитор. Причины, корректирующие действия, ответственные, сроки и доказательства заполняются в ПКПД.")}</div>
            ) : null}

            {/* Поля только для аудитора/админа — для customer показываем как read-only справку */}
            {isCustomer ? (
              <>
                <div className="form-group">
                  <label className="form-label">{uiText("Название")}</label>
                  <div className="ap-control" style={{ background: '#fafafa' }}>{form.title || '—'}</div>
                </div>
                <div className="form-group">
                  <label className="form-label">{uiText("Описание")}</label>
                  <div className="ap-control" style={{ background: '#fafafa', minHeight: 80, whiteSpace: 'pre-wrap' }}>{form.description || '—'}</div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{uiText("Классификация")}</label>
                    <div className="ap-control" style={{ background: '#fafafa' }}>
                      {classificationLabel(form.priority, issue?.classification_scheme)}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{uiText("Статус")}</label>
                    <div className="ap-control" style={{ background: '#fafafa' }}>
                      {STATUS_META[form.status]?.label || form.status}
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">{uiText("Требование / ссылка")}</label>
                  <div className="ap-control" style={{ background: '#fafafa' }}>{form.requirement_ref || '—'}</div>
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label required">{uiText("Название")}</label>
                  <input className="ap-control" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">{uiText("Описание")}</label>
                  <textarea className="ap-control" rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">{uiText("Требование / ссылка")}</label>
                  <input className="ap-control" value={form.requirement_ref} onChange={e => setForm({ ...form, requirement_ref: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{uiText("Классификация")}</label>
                    <select className="ap-control" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                      {issue?.classification_scheme === 'compliance-v2' ? <>
                        <option value="major">{uiText("Значительное (Major)")}</option><option value="minor">{uiText("Незначительное (Minor)")}</option>
                        <option value="ofi">{uiText("Возможность для улучшения (OFI)")}</option><option value="sp">{uiText("Сильная сторона (SP)")}</option>
                      </> : <>
                      <option value="critical">{uiText("Критическое (КН)")}</option>
                      <option value="significant">{uiText("Значительное (ЗН)")}</option>
                      <option value="minor">{uiText("Незначительное (НН)")}</option>
                      <option value="recommendation">{uiText("Рекомендация аудитора")}</option>
                      </>}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{uiText("Статус")}</label>
                    <select className="ap-control" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                      {EDITABLE_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </div>
                </div>
              </>
            )}

            {!isCustomer ? <div className="form-row">
              <div className="form-group">
                <label className="form-label">{uiText("Ответственный по умолчанию для ПКПД")}</label>
                <select className="ap-control" value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                  <option value="">{uiText("Не назначен")}</option>
                  {usersForDropdown.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{uiText("Директивный срок ПКПД")}</label>
                <SafeDateInput value={form.due_date} onChange={value => setForm({ ...form, due_date: value })} />
              </div>
            </div> : null}
          </div>
          <div className="ap-dialog__footer">
            <button type="button" className="ap-button ap-button--secondary" disabled={saving} onClick={onClose}>{isCustomer ? uiText("Закрыть") : uiText("Отмена")}</button>
            {!isCustomer ? <button type="submit" className="ap-button ap-button--primary" disabled={saving}>{saving ? uiText("Сохраняю…") : uiText("Сохранить")}</button> : null}
          </div>
        </form>
    </Dialog>
  );
}

function StatTile({ color, bg, border, value, label, tone }) {
  return (
    <div className={`issues-stat-tile issues-stat-tile-${tone}`} style={{
      background: bg, border: `1px solid ${border}`, borderRadius: 10,
      padding: "16px 18px", transition: "transform 0.15s, box-shadow 0.15s",
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 12px -2px rgba(0,0,0,0.08)"; }}
    onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
      <div style={{ fontSize: 32, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color, opacity: 0.8, marginTop: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
    </div>
  );
}

export default function IssuesPage() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [issues, setIssues] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') || 'all');
  const [onlyOverdue, setOnlyOverdue] = useState(searchParams.get('overdue') === '1');
  const [onlyDueSoon, setOnlyDueSoon] = useState(searchParams.get('due_soon') === '1');
  const [assigneeFilter, setAssigneeFilter] = useState(searchParams.get('assignee_id') || 'all');
  const [error, setError] = useState('');
  const [editingIssue, setEditingIssue] = useState(null);
  const [preview, setPreview] = useState(null);
  const { settings, setSettings, visible } = useRegistrySettings('issues', REGISTRY_COLUMNS);

  useEffect(() => setSearch(searchParams.get('search') || ''), [searchParams]);
  useEffect(() => setStatusFilter(searchParams.get('status') || 'all'), [searchParams]);
  useEffect(() => setPriorityFilter(searchParams.get('priority') || 'all'), [searchParams]);
  useEffect(() => setOnlyOverdue(searchParams.get('overdue') === '1'), [searchParams]);
  useEffect(() => setOnlyDueSoon(searchParams.get('due_soon') === '1'), [searchParams]);
  useEffect(() => setAssigneeFilter(searchParams.get('assignee_id') || 'all'), [searchParams]);

  const syncParams = (next = {}) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '' || value === 'all' || value === false) params.delete(key);
      else params.set(key, String(value));
    });
    setSearchParams(params);
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      // Для дропдауна ответственного нужны сотрудники проверяемых организаций,
      // включая приглашённых, но ещё не активированных пользователей.
      const [issuesRes, usersRes] = await Promise.all([
        issuesApi.list(),
        user?.role === 'admin' || user?.role === 'auditor' ? usersApi.customers() : Promise.resolve({ data: [] }),
      ]);
      setIssues(issuesRes.data || []);
      setUsers(usersRes.data || []);
    } catch (e) {
      console.error(e);
      setError(getApiErrorMessage(e));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const now = new Date();
    const in7days = new Date();
    in7days.setDate(in7days.getDate() + 7);
    return issues.filter(issue => {
      const q = search.trim().toLowerCase();
      const matchesSearch = !q || (issue.title || '').toLowerCase().includes(q) || (issue.description || '').toLowerCase().includes(q) || String(issue.audit_id).includes(q) || (issue.assignee?.full_name || '').toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all' || statusGroupOf(issue.status) === statusFilter;
      const matchesPriority = priorityFilter === 'all' || issue.priority === priorityFilter;
      const matchesAssignee = assigneeFilter === 'all' || String(issue.assignee?.id || '') === assigneeFilter;
      const dueDate = issue.due_date ? new Date(issue.due_date) : null;
      const isOverdue = dueDate && !TERMINAL_STATUSES.has(issue.status) && dueDate < now;
      const isDueSoon = dueDate && !TERMINAL_STATUSES.has(issue.status) && dueDate >= now && dueDate <= in7days;
      return matchesSearch && matchesStatus && matchesPriority && matchesAssignee && (!onlyOverdue || isOverdue) && (!onlyDueSoon || isDueSoon);
    });
  }, [issues, priorityFilter, search, statusFilter, assigneeFilter, onlyOverdue, onlyDueSoon]);

  const counts = {
    open: issues.filter(i => statusGroupOf(i.status) === 'open').length,
    in_progress: issues.filter(i => statusGroupOf(i.status) === 'in_progress').length,
    resolved: issues.filter(i => statusGroupOf(i.status) === 'resolved').length,
    closed: issues.filter(i => statusGroupOf(i.status) === 'closed').length,
  };
  const isCustomerUser = isAuditeeRole(user);
  const isStaffUser = user?.role === 'admin' || user?.role === 'auditor';

  const onSaveIssue = async (payload) => {
    try {
      await issuesApi.update(editingIssue.id, payload);
      setEditingIssue(null);
      await load();
      return true;
    } catch (e) {
      console.error(e);
      alert(getApiErrorMessage(e));
      return false;
    }
  };

  const onDeleteIssue = async (issue) => {
    if (!await confirm(uiText("Удалить замечание «{v0}»?", {v0: (issue.title)}), { title: uiText("Удаление замечания"), confirmLabel: uiText("Удалить") })) return;
    try {
      await issuesApi.delete(issue.id);
      await load();
    } catch (e) {
      console.error(e);
      alert(getApiErrorMessage(e));
    }
  };


  const onExport = async () => {
    try {
      const res = await opsApi.issuesXlsx();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'corrective_actions.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert(getApiErrorMessage(e));
    }
  };

  return (
    <div className={registryStyles.workspace} data-density={settings.density}>
      <PageHeader
        icon={AlertTriangle}
        iconColor="#b91c1c"
        title={uiText("Замечания")}
        subtitle={uiText("Единый реестр проблем, сроков и исполнителей по всем аудитам. Фильтруйте по статусу, приоритету и аудиту.")}
        right={
          <>
            <button type="button" className="btn btn-outline" onClick={() => navigate('/corrective-actions')}>
              <ArrowLeftRight size={14}/>{uiText(" Мониторинг")}</button>
            {isStaffUser && (
              <button type="button" className="btn btn-primary" onClick={onExport}>
                <Download size={14}/>{uiText(" Экспорт XLSX")}</button>
            )}
          </>
        }
      />

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      <RegistryPreferences scope="issues" columns={REGISTRY_COLUMNS} filters={{search,status:statusFilter,priority:priorityFilter,assignee_id:assigneeFilter,overdue:onlyOverdue?'1':'',due_soon:onlyDueSoon?'1':''}} settings={settings} onSettings={setSettings} allowSort onApply={query => setSearchParams(Object.fromEntries(['search','status','priority','assignee_id','overdue','due_soon'].filter(key => typeof query[key] === 'string' && query[key] && query[key] !== 'all').map(key => [key,query[key]])))} />

      <div className="stats-grid">
        <StatTile tone="open" color="#b91c1c" bg="linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)" border="#fca5a5" value={counts.open} label={uiText("Открытые")}/>
        <StatTile tone="progress" color="#1d4ed8" bg="linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)" border="#93c5fd" value={counts.in_progress} label={uiText("В работе")}/>
        <StatTile tone="resolved" color="#15803d" bg="linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)" border="#86efac" value={counts.resolved} label={uiText("Решённые")}/>
        <StatTile tone="closed" color="#475569" bg="linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)" border="#cbd5e1" value={counts.closed} label={uiText("Закрытые")}/>
      </div>

      <div className="card issues-filter-card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <div className="filter-bar issues-filter-bar">
            <div className="search-bar" style={{ flex: 1, minWidth: 240 }}>
              <input
                placeholder={uiText("Поиск по названию, описанию, исполнителю, ID аудита…")}
                value={search}
                onChange={e => { const v = e.target.value; setSearch(v); syncParams({ search: v }); }}
              />
              <span className="search-bar-icon" aria-hidden="true"><Search /></span>
            </div>
            <select className="form-control" style={{ width: 180 }} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); syncParams({ status: e.target.value }); }}>
              {STATUS_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
            <select className="form-control" style={{ width: 180 }} value={priorityFilter} onChange={e => { setPriorityFilter(e.target.value); syncParams({ priority: e.target.value }); }}>
              {PRIORITY_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
            <select className="form-control" style={{ width: 220 }} value={assigneeFilter} onChange={e => { setAssigneeFilter(e.target.value); syncParams({ assignee_id: e.target.value }); }}>
              <option value="all">{uiText("Все исполнители")}</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={onlyOverdue} onChange={e => { setOnlyOverdue(e.target.checked); if (e.target.checked) setOnlyDueSoon(false); syncParams({ overdue: e.target.checked ? '1' : '', due_soon: '' }); }} />{uiText("Только просроченные")}</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={onlyDueSoon} onChange={e => { setOnlyDueSoon(e.target.checked); if (e.target.checked) setOnlyOverdue(false); syncParams({ due_soon: e.target.checked ? '1' : '', overdue: '' }); }} />{uiText("Срок ≤ 7 дней")}</label>
          </div>
          {(search || statusFilter !== 'all' || priorityFilter !== 'all' || assigneeFilter !== 'all' || onlyOverdue || onlyDueSoon) && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              {search && <ActiveFilterChip label={uiText("Поиск: {v0}", {v0: (search)})} onRemove={() => syncParams({ search: '' })} />}
              {statusFilter !== 'all' && <ActiveFilterChip label={uiText("Статус: {v0}", {v0: (STATUS_META[statusFilter]?.label || statusFilter)})} onRemove={() => syncParams({ status: '' })} />}
              {priorityFilter !== 'all' && <ActiveFilterChip label={uiText("Приоритет: {v0}", {v0: (priorityFilter)})} onRemove={() => syncParams({ priority: '' })} />}
              {assigneeFilter !== 'all' && <ActiveFilterChip label={uiText("Исполнитель: {v0}", {v0: (users.find((u) => String(u.id) === assigneeFilter)?.full_name || assigneeFilter)})} onRemove={() => syncParams({ assignee_id: '' })} />}
              {onlyOverdue && <ActiveFilterChip label={uiText("Только просроченные")} onRemove={() => syncParams({ overdue: '' })} />}
              {onlyDueSoon && <ActiveFilterChip label={uiText("Срок ≤ 7 дней")} onRemove={() => syncParams({ due_soon: '' })} />}
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setSearchParams({})}>{uiText("Сбросить все фильтры")}</button>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">{uiText("Список замечаний")}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{uiText("Показано: ")}{filtered.length}</div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div className="empty-state">{uiText("Загрузка…")}</div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <AlertTriangle size={36} color="#ddd" />
              <div style={{ marginTop: 8 }}>{uiText("По текущим фильтрам замечания не найдены.")}</div>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{uiText("Замечание")}</th>
                    <th data-column="audit" hidden={!visible('audit')}>{uiText("Аудит")}</th>
                    <th data-column="priority" hidden={!visible('priority')}>{uiText("Приоритет")}</th>
                    <th data-column="status" hidden={!visible('status')}>{uiText("Статус")}</th>
                    <th data-column="assignee" hidden={!visible('assignee')}>{uiText("Исполнитель")}</th>
                    <th data-column="due" hidden={!visible('due')}>{uiText("Срок ПКПД")}</th>
                    <th style={{ width: 120 }}>{uiText("Действия")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortRegistryRows(filtered,settings.sort).map(issue => {
                    const st = STATUS_META[issue.status] || { label: issue.status, cls: 'badge-gray' };
                    const prCls = ['critical','high','major'].includes(issue.priority) ? 'badge-red' : ['medium','minor','significant'].includes(issue.priority) ? 'badge-orange' : issue.priority === 'sp' ? 'badge-green' : issue.priority === 'ofi' ? 'badge-blue' : 'badge-gray';
                    const overdue = issue.due_date && !TERMINAL_STATUSES.has(issue.status) && new Date(issue.due_date) < new Date();
                    return (
                      <tr
                        key={issue.id}
                        data-clickable="true"
                        tabIndex={0}
                        onClick={event => { event.currentTarget.focus({ preventScroll: true }); setPreview(issue); }}
                        onKeyDown={(event) => {
                          if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                            event.preventDefault();
                            setPreview(issue);
                          }
                        }}
                      >
                        <td>
                          <div style={{ fontWeight: 600 }}>{issue.title}</div>
                          {issue.description && <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{issue.description.slice(0, 90)}</div>}
                          {issue.requirement_ref && <div style={{ fontSize: 12, color: '#4b5563', marginTop: 2 }}>{uiText("Требование: ")}{issue.requirement_ref}</div>}
                        </td>
                        <td data-column="audit" hidden={!visible('audit')}>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={event => { event.stopPropagation(); navigate(`/audits/${issue.audit_id}`); }}>
                            #{issue.audit_id}
                          </button>
                        </td>
                        <td data-column="priority" hidden={!visible('priority')}><span className={`badge ${prCls}`}>{classificationLabel(issue.priority, issue.classification_scheme)}</span></td>
                        <td data-column="status" hidden={!visible('status')}>
                          <StatusBadge
                            status={issue.status}
                            type="issue"
                            label={st.label}
                            variant={variantFromBadgeClass(st.cls)}
                          />
                        </td>
                        <td data-column="assignee" hidden={!visible('assignee')} style={{ fontSize: 12 }}>{issue.assignee?.full_name || '—'}</td>
                        <td data-column="due" hidden={!visible('due')}>
                          <div style={{ fontSize: 12, color: overdue ? '#C0392B' : '#555', fontWeight: overdue ? 600 : 400 }}>{formatDate(issue.due_date)}</div>
                        </td>
                        <td onClick={(event) => event.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button type="button" className="btn btn-outline btn-sm" onClick={event => { event.currentTarget.focus({ preventScroll: true }); setPreview(issue); }}>{uiText("Просмотр")}</button>
                            {isCustomerUser ? (
                              <button type="button" className="btn btn-outline btn-sm" onClick={() => navigate(`/corrective-plans?audit_id=${issue.audit_id}`)}>{uiText("ПКПД")}</button>
                            ) : (
                              <>
                                <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditingIssue(issue)}>
                                  <Edit2 size={13} />{uiText(" Открыть")}</button>
                                {isStaffUser ? (
                                  <ActionMenu
                                    label={uiText("Другие действия с замечанием {v0}", {v0: (issue.title)})}
                                    actions={[
                                      {
                                        key: 'delete',
                                        label: uiText("Удалить замечание"),
                                        icon: Trash2,
                                        danger: true,
                                        onClick: () => onDeleteIssue(issue),
                                      },
                                    ]}
                                  />
                                ) : null}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editingIssue && <IssueModal issue={editingIssue} users={users} onClose={() => setEditingIssue(null)} onSave={onSaveIssue} currentUserRole={user?.role} />}
      {preview && <RecordPreview kind="issue" record={preview} onClose={() => setPreview(null)} onEdit={isStaffUser ? () => { setEditingIssue(preview); setPreview(null); } : null} />}
    </div>
  );
}
